/**
 * Routing rule for the signed-note appointment lock.
 *
 * The lock itself is enforced by the appointment-tools bot (it can trust the
 * caller identity). Direct FHIR writes from the browser cannot, so any appointment
 * that carries a clinical note must be updated through the bot.
 */

import type { MedplumClient } from '@medplum/core';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { APPOINTMENT_TAG_SYSTEM } from '@unify/fhir-model/constants';
import {
  clinicalNoteLockSlice,
  signedNoteBlocksAppointmentEdit,
} from '@unify/fhir-model/resources/questionnaire-response';
import { UserFacingError } from '@/lib/userFacingError';

export const SIGNED_NOTE_APPOINTMENT_LOCK_MESSAGE =
  'This appointment is locked because the linked clinical note is signed.';

/** True when at least one clinical note is linked to the appointment. */
export function appointmentNotesRequireScheduleBot(
  notes: readonly QuestionnaireResponse[],
): boolean {
  return notes.length > 0;
}

/**
 * The one paginated fetcher for notes tagged to an appointment: yields every page, so a note
 * past the first page is never missed. Lookup errors propagate to the caller.
 */
export async function* appointmentNotePages(
  medplum: Pick<MedplumClient, 'searchResourcePages'>,
  appointmentId: string,
  options?: { cache?: 'no-cache' },
): AsyncGenerator<QuestionnaireResponse[]> {
  yield* medplum.searchResourcePages('QuestionnaireResponse', {
    _tag: `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`,
    _count: '25',
  }, options);
}

/** True on the first note (across all pages) matching `predicate`. */
export async function someAppointmentNote(
  medplum: Pick<MedplumClient, 'searchResourcePages'>,
  appointmentId: string,
  predicate: (note: QuestionnaireResponse) => boolean,
  options?: { cache?: 'no-cache' },
): Promise<boolean> {
  for await (const page of appointmentNotePages(medplum, appointmentId, options)) {
    if (page.some(predicate)) return true;
  }
  return false;
}

/** Look up notes tagged to the appointment. Failures route through the bot (fail closed). */
export async function appointmentRequiresScheduleBot(
  medplum: Pick<MedplumClient, 'searchResources'>,
  appointmentId: string,
): Promise<boolean> {
  try {
    const notes = (await medplum.searchResources('QuestionnaireResponse', {
      _tag: `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`,
      _count: '1',
    })) as QuestionnaireResponse[];
    return appointmentNotesRequireScheduleBot(notes);
  } catch (err) {
    console.warn('[signedNoteAppointmentLock] Note lookup failed; routing through bot:', err);
    return true;
  }
}

/**
 * Guard for the one write that cannot go through the bot: drag-reschedule of a room-less
 * appointment, which PUTs `start`/`end` directly. Re-reads the notes at write time rather
 * than trusting the calendar's cached `linkedNoteSigned` flag. Throws when locked, and also
 * when the lookup fails (fail closed).
 */
export async function assertAppointmentUnlockedForDirectWrite(
  medplum: Pick<MedplumClient, 'searchResourcePages'>,
  appointmentId: string,
  actorPractitionerId: string | undefined,
): Promise<void> {
  const blocked = await someAppointmentNote(medplum, appointmentId, (note) =>
    signedNoteBlocksAppointmentEdit([clinicalNoteLockSlice(note)], actorPractitionerId),
  );
  if (blocked) {
    throw new UserFacingError(SIGNED_NOTE_APPOINTMENT_LOCK_MESSAGE);
  }
}
